<template>
  <div class="entity-prop-layer">
    <div
      :class="['entity-property', { active: isActive }]"
      @click="setActiveLayer()"
    >
      <icon size="18" class="segment-icon" @click="setActiveLayer()">{{
        isActive ? "visibility" : "visibility_off"
      }}</icon>
      <input
        type="text"
        ref="layerName"
        :class="['title', { 'disable-clicks': !data.isEditable }, 'ellipsis']"
        v-model="layerName"
        @focus="$event.target.select()"
        @blur="updateLayerName"
        @click.stop
      />
      <div class="flex-expand" />
      <span class="layer-value">{{ formattedValue || 0 }}</span>
      <icon
        v-if="data.isEditable"
        size="24"
        type="button"
        :class="[
          'delete-icon',
          { rotate: showEditor },
          { 'show-on-hover': !showEditor },
        ]"
        @click="expandProperties()"
        >keyboard_arrow_down</icon
      >
      <!-- <icon size="18" class="visibility-icon">visibility</icon> -->
    </div>
    <transition name="slide">
      <div v-if="data.isEditable && showEditor" class="property-details">
        <label>Entity property</label>
        <SearchableDropDown
          placeholder="Choose property"
          v-model="propColumn"
          :options="properties"
        />
        <label>Aggregation</label>
        <DropDown
          v-model="propAggregation"
          :options="columnAggregations"
          placeholder="Choose Aggregation"
        />
        <div class="prop-actions">
          <button class="prop-action-button" @click="deleteLayer(data)">
            <Icon size="18">delete</Icon>delete layer
          </button>
          <button
            :disabled="isButtonDisabled"
            class="prop-action-button save"
            @click="saveLayer(data)"
          >
            <Icon size="18">check</Icon>save layer
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SearchableDropDown from "@/components/metrics/views/SearchableDropDown";
import { format } from "@/lib/numberFormatter";
// import { startTour } from "@/components/metrics/tours/EntityPropLayer.tour";
// import { SlideYUpTransition } from "vue2-transitions";

export default {
  props: {
    data: Object,
    properties: Array,
    aggregations: Array || null,
    isActive: Boolean,
  },
  components: {
    SearchableDropDown,
    // SlideYUpTransition
  },
  data() {
    return {
      showEditor: false,
      layerName: this.data.name,
    };
  },
  computed: {
    ...mapGetters("StudioManager", ["getConfig"]),
    isButtonDisabled() {
      return !(this.propColumn && this.propAggregation);
    },
    formattedValue() {
      if (!this.data.value) return null;
      let value = parseFloat(this.data.value);
      value = isNaN(value) ? 0 : value;
      value = format(value.toFixed(3), 2);
      return value;
    },
    propColumn: {
      get() {
        return this.data.property;
      },
      set(property) {
        if (this.propAggregation) {
          this.layerName = `${this.propAggregation.name} ${property.name}`;
          this.updateLayerName();
        }
        this.updateLayer({
          ...this.data,
          property,
        });

        if (
          this.propAggregation &&
          !property.aggs
            .map((item) => item.id)
            .includes(this.propAggregation.id)
        ) {
          this.propAggregation = property.aggs ? property.aggs[0] : null;
        }
      },
    },
    columnAggregations() {
      // check if the selected property already contains any aggregations
      if (this.data.property && this.data.property.aggs) {
        return this.data.property.aggs;
      } else {
        return this.aggregations;
      }
    },
    propAggregation: {
      get() {
        return this.data.aggregation;
      },
      set(aggregation) {
        if (this.propColumn && aggregation) {
          this.layerName = `${aggregation.name} ${this.propColumn.name}`;
          this.updateLayerName();
        }
        this.updateLayer({
          ...this.data,
          aggregation,
        });
      },
    },
  },
  methods: {
    ...mapActions("LayerManager", ["updateLayer", "deleteLayer"]),
    expandProperties() {
      this.showEditor = !this.showEditor;
    },
    setActiveLayer() {
      this.$emit("setActive", this.data);
    },
    updateLayerName() {
      this.updateLayer({
        ...this.data,
        name: this.layerName,
      });
    },
    saveLayer() {
      this.setActiveLayer();
      this.expandProperties();
    },
  },
  mounted() {
    // if (this.$route.fullPath === "/studio/edit") {
    //   startTour({ check: false });
    //   this.showEditor = true;
    // }

    if (!(this.propAggregation && this.propColumn)) {
      this.showEditor = true;
    } else {
      this.showEditor = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.entity-prop-layer {
  border-bottom: 1px solid var(--border-color);
}

.property-details {
  padding: 5px 20px 10px 60px;
  display: flex;
  flex-direction: column;

  label {
    padding-bottom: 7px;
  }

  .prop-actions {
    display: flex;
    flex-direction: row;
    padding: 15px 0 0 0;

    .prop-action-button {
      display: flex;
      align-items: center;
      padding: 10px;
      border: 0;
      border-radius: 3px;
      background: var(--color-default-background);
      color: var(--font-color);
      text-transform: capitalize;
      transition: 0.3s all ease-in-out;

      &.save {
        background-color: var(--color-primary);
        color: var(--color-secondary);
        margin-left: 10px;

        &:hover {
          opacity: 0.8;
          color: var(--color-secondary);
        }
      }

      i {
        margin-right: 5px;
        pointer-events: none;
      }

      &:hover {
        cursor: pointer;
        color: var(--color-error);
      }

      &:active {
        transform: scale(0.95);
      }

      &:disabled {
        background: var(--color-secondary-light);
        color: var(--font-color);
        opacity: 0.7;
        cursor: not-allowed;

        &:hover {
          color: var(--font-color);
        }
      }
    }
  }
}

.entity-property {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 10px;
  border-left: 3px solid var(--color-transparent);
  transition: 0.3s background ease-in-out;

  .segment-icon {
    padding: 10px;
    background: var(--color-secondary-light);
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0.6 !important;
    border-radius: 5px;

    &:hover {
      color: var(--color-primary);
    }
  }

  .layer-value {
    padding: 0 5px;
  }

  &.active .segment-icon {
    opacity: 1 !important;
    background: var(--color-primary-light);
  }

  input.title {
    margin: 0;
    font-weight: normal;
    padding: 12px 10px;
    margin: 7px 0;
    min-width: 0;
    width: auto;
    background: transparent;
    border: 1px solid transparent;

    &:focus {
      border: 1px solid var(--color-accent);
    }
  }

  .disable-clicks {
    pointer-events: none;
  }

  p.caption {
    margin: 0;
  }

  .show-on-hover {
    display: none;
  }

  &:hover {
    padding: 0px 5px 0px 10px;
    cursor: pointer;
    color: var(--color-primary);

    input {
      color: var(--color-primary);
    }
    .show-on-hover {
      display: flex;
    }
    // .delete-icon {
    //   display: flex !important;
    // }
  }

  .segment-icon {
    opacity: 0.8;
    color: var(--font-color);
  }

  .visibility-icon,
  .delete-icon {
    color: var(--color-primary);
    transition: 0.3s all ease-in-out;
    // display: none;
  }

  .rotate {
    transform: rotate(180deg);
  }

  .visibility-icon {
    padding: 7px;
  }

  &.active {
    border-left: 3px solid var(--color-primary);
    background: var(--border-color);
    .title,
    .segment-icon {
      color: var(--color-primary);
    }

    .layer-value {
      color: var(--color-primary);
    }

    .visibility-icon {
      display: flex;
    }

    &:hover {
      .visibility-icon {
        display: none;
      }
    }
  }
}
</style>
